import { APIComponentAttributes, ArdoqId } from '@ardoq/api-types';
import { ButtonWithDropdown, DropdownOptionType } from '@ardoq/dropdown-menu';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import {
  getNonPrioritySurveys,
  getPrioritySurvey,
  ComponentSurveyWithOptionalApprovalStatus,
} from './SurveyButtons';
import { ButtonType } from '@ardoq/button';
import { isTruthy } from '@ardoq/common-helpers';
import { markdownToTextPreview } from '@ardoq/markdown';
import {
  ChangeRequestIcon,
  DEFAULT_CHANGE_REQUEST_TOOLTIP,
} from './ChangeRequestIcon';

export const getSurveyOptions = (
  prioritySurvey: ComponentSurveyWithOptionalApprovalStatus | undefined,
  surveys: ComponentSurveyWithOptionalApprovalStatus[],
  onSurveyClick: (surveyId: ArdoqId) => void,
  changeRequestTooltip: string = DEFAULT_CHANGE_REQUEST_TOOLTIP
) => {
  const prioritySurveyOptions = prioritySurvey
    ? [
        {
          label: prioritySurvey.name,
          description: prioritySurvey.description
            ? markdownToTextPreview(prioritySurvey.description)
            : undefined,
          truncateTitle: true,
          truncateDescription: true,
          ...(prioritySurvey.changeRequest && {
            leftContent: <ChangeRequestIcon tooltip={changeRequestTooltip} />,
          }),
          onClick: () => onSurveyClick(prioritySurvey._id),
        },
        { type: DropdownOptionType.DIVIDER },
      ]
    : [];
  return [
    ...prioritySurveyOptions,
    ...surveys.map(survey => ({
      label: survey.name,
      description: survey.description
        ? markdownToTextPreview(survey.description)
        : undefined,
      truncateTitle: true,
      truncateDescription: true,
      ...(survey.changeRequest && {
        leftContent: <ChangeRequestIcon tooltip={changeRequestTooltip} />,
      }),
      onClick: () => onSurveyClick(survey._id),
    })),
  ].filter(isTruthy);
};

type SurveysDropdownProps = {
  component: APIComponentAttributes;
  surveys: ComponentSurveyWithOptionalApprovalStatus[];
  onSurveyClick: (surveyId: ArdoqId) => void;
  submitUpdateLabel?: string;
  // Localized tooltip + accessible label rendered on the change-request icon.
  // Defaults to English; callers with i18n context should pass a translated string.
  changeRequestTooltip?: string;
};

const SurveysDropdown = ({
  component,
  surveys,
  onSurveyClick,
  submitUpdateLabel = 'Submit an update',
  changeRequestTooltip = DEFAULT_CHANGE_REQUEST_TOOLTIP,
}: SurveysDropdownProps) => {
  const prioritySurvey = getPrioritySurvey(component, surveys);
  const otherSurveys = getNonPrioritySurveys(prioritySurvey, surveys);
  const options = getSurveyOptions(
    prioritySurvey,
    otherSurveys,
    onSurveyClick,
    changeRequestTooltip
  );
  return (
    <ButtonWithDropdown
      dropdownSize={DropdownSize.S}
      alignMenuTo="left"
      dataTestId="surveys-dropdown"
      options={options}
      buttonType={ButtonType.SECONDARY}
    >
      {submitUpdateLabel}
    </ButtonWithDropdown>
  );
};

export { SurveysDropdown };
